import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Loader2, CheckCircle2, Building2, Briefcase, MapPin, Award, FileText } from 'lucide-react';

export function StepZusammenfassung({ form, gewaehlteStufe, preisstufen, sending, onAbsenden, istBearbeitung }) {
  const { t } = useLanguage();
  const stufe = preisstufen.find(s => s.name === gewaehlteStufe);

  const sections = [
    {
      icon: Building2,
      title: t('dlWizard.step1Title'),
      items: [
        { label: t('dlWizard.firmaLabel'), value: form.firma },
        { label: t('dlWizard.kontaktLabel'), value: form.kontakt_name },
        { label: t('dlWizard.emailLabel'), value: form.kontakt_email },
        form.kontakt_telefon && { label: t('dlWizard.telefonLabel'), value: form.kontakt_telefon },
      ].filter(Boolean),
    },
    {
      icon: Briefcase,
      title: t('dlWizard.step2Title'),
      items: [{ label: null, value: form.leistungen.join(', ') || t('dlWizard.keineAngaben') }],
    },
    {
      icon: MapPin,
      title: t('dlWizard.step3Title'),
      items: [
        { label: t('dlWizard.bundeslaenderLabel'), value: form.bundeslaender.join(', ') || t('dlWizard.keineAngaben') },
        form.einsatzradius_km && { label: t('dlWizard.einsatzradiusLabel'), value: `${form.einsatzradius_km} ${t('dlWizard.km')}` },
      ].filter(Boolean),
    },
    {
      icon: Award,
      title: t('dlWizard.stepZertifikate'),
      items: [
        form.zertifizierungen.length > 0 && { label: t('dlWizard.zertLabel'), value: form.zertifizierungen.join(', ') },
        form.zertifizierungen_freitext && { label: t('dlWizard.zertFreitextLabel'), value: form.zertifizierungen_freitext },
      ].filter(Boolean),
    },
    form.beschreibung && {
      icon: FileText,
      title: t('dlWizard.beschreibungLabel'),
      items: [{ label: null, value: form.beschreibung.length > 200 ? form.beschreibung.slice(0, 200) + '...' : form.beschreibung }],
    },
  ].filter(Boolean);

  return (
    <div className="space-y-5" data-testid="step-zusammenfassung">
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="h-6 w-6 text-primary" />
        </div>
        <h3 className="font-serif text-xl font-bold text-foreground">{t('dlWizard.step5Title')}</h3>
        <p className="text-sm text-muted-foreground mt-1">{t('dlWizard.step5Desc')}</p>
      </div>

      <div className="space-y-3">
        {sections.map((sec, i) => (
          <Card key={i} className="border-warm-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <sec.icon className="h-4 w-4 text-primary" />
                <h4 className="text-sm font-semibold text-foreground">{sec.title}</h4>
              </div>
              <div className="space-y-1">
                {sec.items.map((item, j) => (
                  <p key={j} className="text-sm text-muted-foreground">
                    {item.label && <span className="text-foreground font-medium">{item.label}: </span>}
                    {item.value}
                  </p>
                ))}
                {sec.items.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">{t('dlWizard.keineAngaben')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stufe && (
        <div className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/[0.03]">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">{t('dlWizard.gewaehlteStufe')}: {gewaehlteStufe}</span>
          </div>
          <Badge variant="outline" className="text-primary border-primary/30">{stufe.preis}</Badge>
        </div>
      )}

      <Button
        onClick={onAbsenden}
        disabled={sending}
        className="w-full h-12 text-base"
        data-testid="profil-absenden-btn"
      >
        {sending ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {istBearbeitung ? t('dlWizard.wirdGespeichert') : t('dlWizard.wirdErstellt')}</>
        ) : (
          <><Shield className="h-4 w-4 mr-2" /> {istBearbeitung ? t('dlWizard.aenderungenSpeichern') : `${t('dlWizard.profilErstellen')} · ${stufe?.preis || 'Kostenlos'}`}</>
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        {gewaehlteStufe === 'Basis' ? t('dlWizard.basisHint') : `${t('dlWizard.gewaehlteStufe')} "${gewaehlteStufe}" – ${stufe?.preis || ''}`}
      </p>
    </div>
  );
}
